import { useEffect, useState } from 'react'
import { ACTIVITY_AGE_LABEL, type MeResponse, type RoomCard } from '@cs/shared'
import { api } from '../api.js'
import { describeError } from '../App.js'

const ROLE_LABEL: Record<RoomCard['role'], string> = {
  owner: 'オーナー',
  member: 'メンバー',
  viewer: '閲覧',
}

/**
 * The landing page: one panel per project.
 *
 * It replaces a dropdown in the app bar. A dropdown is fine for switching between
 * two things you are already thinking about, but it hides the one question this
 * person actually opens the browser with — "is anything waiting for me anywhere" —
 * behind a click per project. The panels answer that before anything is chosen.
 *
 * Counts come from `/v1/summaries` rather than from opening each project, because
 * opening a project marks its published summaries as delivered, and glancing at a
 * list must not quietly take away somebody else's ability to retract one.
 */
export function Home({
  me,
  onOpen,
  onHelp,
}: {
  me: MeResponse
  onOpen: (roomId: string) => void
  onHelp: () => void
}) {
  const [rooms, setRooms] = useState<RoomCard[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .summaries()
      .then((r) => setRooms(r.rooms))
      .catch((err) => setError(describeError(err)))
  }, [])

  const active = (rooms ?? []).filter((r) => !r.archived)
  const archived = (rooms ?? []).filter((r) => r.archived)

  return (
    <div className="wrap">
      {error && <div className="error">{error}</div>}

      <h1>プロジェクト</h1>
      <p className="muted" style={{ marginTop: -4 }}>
        {me.person.display_name} さん（{me.person.org}）。
        プロジェクトごとに相手の会社が違います。開くものを選んでください。
      </p>

      {rooms === null && <p className="muted">読み込み中…</p>}

      {rooms !== null && active.length === 0 && (
        <div className="card">
          <h2>参加しているプロジェクトがありません</h2>
          <p className="muted">招待コードを受け取ったら、参加画面から参加してください。</p>
        </div>
      )}

      <div className="panel-grid">
        {active.map((r) => (
          <button key={r.id} className="panel" onClick={() => onOpen(r.id)}>
            <div className="row" style={{ gap: 8 }}>
              <span className="panel-title">{r.name}</span>
              <span className="pill">{ROLE_LABEL[r.role]}</span>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              メンバー {r.member_count} 人 ・ 最終作業 {ACTIVITY_AGE_LABEL[r.activity.worked]}
            </div>

            <div className="panel-stats">
              <span className="stat">
                <b>{r.questions_for_me}</b>
                <span>あなた宛の質問</span>
              </span>
              <span className="stat">
                <b>{r.open_questions}</b>
                <span>未解決の質問</span>
              </span>
              <span className="stat">
                <b>{r.pending_for_me}</b>
                <span>共有待ち</span>
              </span>
            </div>

            {/* Only shown when there is something to do about it. A row that always
                says "nothing" trains people to stop looking at the row. */}
            {(r.questions_for_me > 0 || r.activity.unshared) && (
              <div className="row" style={{ marginTop: 10 }}>
                {r.questions_for_me > 0 && (
                  <span className="pill warn">回答してください {r.questions_for_me} 件</span>
                )}
                {r.activity.unshared && <span className="pill">未共有の作業あり</span>}
              </div>
            )}
          </button>
        ))}
      </div>

      {archived.length > 0 && (
        <>
          <h2 style={{ marginTop: 32 }}>終了したプロジェクト</h2>
          <div className="panel-grid">
            {archived.map((r) => (
              <button
                key={r.id}
                className="panel"
                style={{ opacity: 0.66 }}
                onClick={() => onOpen(r.id)}
              >
                <div className="panel-title">{r.name}</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  終了済み ・ メンバー {r.member_count} 人
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <div className="card" style={{ marginTop: 32 }}>
        <h2>はじめての方・うまく動かない方へ</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          導入手順、よくある不具合の直し方、文殊自体の更新方法をまとめています。
        </p>
        <button className="primary" onClick={onHelp}>
          使い方とトラブルシュート
        </button>
      </div>
    </div>
  )
}
